import { useEffect, useState } from 'react';
import Layout from '../components/layout';
import Title from '../components/title';
import { supabase } from '../supabase';
import './tablePage.css';
import searchIcon from '../assets/search.svg';
import editIcon from '../assets/edit.svg';
import deleteIcon from '../assets/delete.svg';

const BOOKINGS_PER_PAGE = 4;

const Bookings = () => {
  const [bookings, setBookings] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [eventSettings, setEventSettings] = useState(null);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState('');

  const [ticketId, setTicketId] = useState('');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const [id, setId] = useState('');
  const [editTicketId, setEditTicketId] = useState('');
  const [editFullName, setEditFullName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [isEditOpen, setIsEditOpen] = useState(false);

  const getBookings = async () => {
    const res = await supabase
      .from('bookings')
      .select('id,created_at,ticket_id,full_name,email,tickets(type,price)')
      .order('id', { ascending: false });

    setBookings(res.data || []);
  };

  const getTickets = async () => {
    const res = await supabase.from('tickets').select('id,type').order('id');
    setTickets(res.data || []);
  };

  useEffect(() => {
    getBookings();
    getTickets();
  }, []);

  useEffect(() => {
    const getEventSettings = async () => {
      const { data } = await supabase
        .from('event_settings')
        .select('*')
        .single();

      setEventSettings(data);

      if (data.available_days.length > 0) {
        setSelectedDay(data.available_days[0]);
      }

      const now = new Date();
      const bookingStart = new Date(data.booking_start_date);
      const bookingEnd = new Date(data.booking_end_date);

      setBookingOpen(now >= bookingStart && now <= bookingEnd);
    };

    getEventSettings();
  }, []);

  const createBooking = async (e) => {
    e.preventDefault();

    await supabase.from('bookings').insert([{
      ticket_id: ticketId ? Number(ticketId) : null,
      full_name: fullName,
      email: email,
    }]);

    setTicketId('');
    setFullName('');
    setEmail('');
    setIsCreateOpen(false);
    await getBookings();
  };

  const editBooking = (booking) => {
    setId(booking.id);
    setEditTicketId(booking.ticket_id || '');
    setEditFullName(booking.full_name || '');
    setEditEmail(booking.email || '');
    setIsEditOpen(true);
  };

  const updateBooking = async (e) => {
    e.preventDefault();

    await supabase
      .from('bookings')
      .update({
        ticket_id: editTicketId ? Number(editTicketId) : null,
        full_name: editFullName,
        email: editEmail,
      })
      .eq('id', id);

    setIsEditOpen(false);
    await getBookings();
  };

  const deleteBooking = async (bookingId) => {
    await supabase.from('bookings').delete().eq('id', bookingId);
    await getBookings();
  };

  const filteredBookings = bookings.filter((booking) => {
    const value = search.toLowerCase();
    const ticket = Array.isArray(booking.tickets) ? booking.tickets[0] : booking.tickets;

    return (
      booking.id.toString().includes(value) ||
      booking.full_name?.toLowerCase().includes(value) ||
      booking.email?.toLowerCase().includes(value) ||
      ticket?.type?.toLowerCase().includes(value)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filteredBookings.length / BOOKINGS_PER_PAGE));
  const startIndex = (currentPage - 1) * BOOKINGS_PER_PAGE;
  const paginatedBookings = filteredBookings.slice(startIndex, startIndex + BOOKINGS_PER_PAGE);

  return (
    <Layout>
      <div className="table_page">
        <Title subtitle="Manage bookings" title="Bookings" />

        <div className="table_controls_card">
          <label className="table_search" htmlFor="bookings-search">
            <img alt="" src={searchIcon} />
            <input
              id="bookings-search"
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search bookings..."
              type="text"
              value={search}
            />
          </label>

          <div className="table_controls_actions">
            <span>{filteredBookings.length} total records</span>
            <button
              className="table_primary_button"
              onClick={() => setIsCreateOpen(true)}
              type="button"
            >
              Add Booking
            </button>
          </div>
        </div>

        <div className="table_card">
          <div className="table_scroll">
            <table className="data_table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Ticket</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedBookings.map((booking) => {
                  const ticket = Array.isArray(booking.tickets) ? booking.tickets[0] : booking.tickets;

                  return (
                    <tr key={booking.id}>
                      <td>{booking.id}</td>
                      <td>{booking.full_name}</td>
                      <td>{booking.email}</td>
                    <td>{ticket?.type}</td>
                    <td>
                        <div className="table_actions">
                          <button
                            aria-label={`Edit booking #${booking.id}`}
                            className="table_icon_button"
                            onClick={() => editBooking(booking)}
                            type="button"
                          >
                            <img alt="" src={editIcon} />
                          </button>
                          <button
                            aria-label={`Delete booking #${booking.id}`}
                            className="table_icon_button table_icon_button_danger"
                            onClick={() => deleteBooking(booking.id)}
                            type="button"
                          >
                            <img alt="" src={deleteIcon} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="table_pagination">
            <button disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)} type="button">
              Previous
            </button>
            <span>Page {currentPage} of {totalPages}</span>
            <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)} type="button">
              Next
            </button>
          </div>
        </div>

        {isCreateOpen && (
          <div
            aria-modal="true"
            className="table_modal_overlay"
            onClick={() => setIsCreateOpen(false)}
            role="dialog"
            aria-labelledby="add-booking-heading"
          >
            <div className="table_modal" onClick={(e) => e.stopPropagation()}>
              <div className="table_modal_header">
                <div>
                  <h2 id="add-booking-heading">Add Booking</h2>
                </div>
                <button
                  aria-label="Close add booking form"
                  className="table_modal_close"
                  onClick={() => setIsCreateOpen(false)}
                  type="button"
                >
                  &#x2715;
                </button>
              </div>

              <form className="table_form" onSubmit={createBooking}>
                <div className="table_form_grid">
                  <label className="table_form_field">
                    <span>Ticket</span>
                    <select onChange={(e) => setTicketId(e.target.value)} value={ticketId}>
                      <option value="">Select ticket</option>
                      {tickets.map((ticket) => (
                        <option key={ticket.id} value={ticket.id}>{ticket.type}</option>
                      ))}
                    </select>
                  </label>

                  <label className="table_form_field">
                    <span>Day</span>
                    <select
                      onChange={(e) => setSelectedDay(e.target.value)}
                      value={selectedDay}
                    >
                      {eventSettings?.available_days.map((day) => (
                        <option key={day} value={day}>
                          {day}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="table_form_field">
                    <span>Full Name</span>
                    <input
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder="Full name"
                      value={fullName}
                    />
                  </label>

                  <label className="table_form_field table_form_field_full">
                    <span>Email</span>
                    <input
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Email"
                      value={email}
                    />
                  </label>
                </div>

                <div className="table_form_actions">
                  <button className="table_secondary_button" onClick={() => setIsCreateOpen(false)} type="button">
                    Cancel
                  </button>
                  {bookingOpen ? (
                    <button className="table_primary_button" type="submit">
                      Book Now
                    </button>
                  ) : (
                    <p className="booking_closed">
                      Booking is currently closed
                    </p>
                  )}
                </div>
              </form>
            </div>
          </div>
        )}

        {isEditOpen && (
          <div
            aria-labelledby="edit-booking-heading"
            aria-modal="true"
            className="table_modal_overlay"
            onClick={() => setIsEditOpen(false)}
            role="dialog"
          >
            <div className="table_modal" onClick={(e) => e.stopPropagation()}>
              <div className="table_modal_header">
                <div>
                  <h2 id="edit-booking-heading">Edit Booking</h2>
                </div>
                <button
                  aria-label="Close edit booking form"
                  className="table_modal_close"
                  onClick={() => setIsEditOpen(false)}
                  type="button"
                >
                  &#x2715;
                </button>
              </div>

              <form className="table_form" onSubmit={updateBooking}>
                <div className="table_form_grid">
                  <label className="table_form_field">
                    <span>Ticket</span>
                    <select onChange={(e) => setEditTicketId(e.target.value)} value={editTicketId}>
                      <option value="">Select ticket</option>
                      {tickets.map((ticket) => (
                        <option key={ticket.id} value={ticket.id}>{ticket.type}</option>
                      ))}
                    </select>
                  </label>

                  <label className="table_form_field">
                    <span>Full Name</span>
                    <input
                      onChange={(e) => setEditFullName(e.target.value)}
                      placeholder="Full name"
                      value={editFullName}
                    />
                  </label>

                  <label className="table_form_field table_form_field_full">
                    <span>Email</span>
                    <input
                      onChange={(e) => setEditEmail(e.target.value)}
                      placeholder="Email"
                      value={editEmail}
                    />
                  </label>
                </div>

                <div className="table_form_actions">
                  <button className="table_secondary_button" onClick={() => setIsEditOpen(false)} type="button">
                    Cancel
                  </button>
                  <button className="table_primary_button" type="submit">Save</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Bookings;
